import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import loadingPredictionsIcon from '../../../assets/icons/loading-predictions.png';
import predictionsService from '../../../data/services/predictionsService';
import { DEFAULT_PREVIEW_BADGE } from '../../../data/utils/releaseGate';
import { WarRoom, BestPropsBoard } from './ScoutWarRoom';
import { getTeamById } from '../../../data/constants/apiConstants';
import ScoutAiButton from '../Stats/mlb-schedule/components/ScoutAiButton';
import ScoutAiModal  from '../Stats/mlb-schedule/components/ScoutAiModal';
import PredictionsNav from './PredictionsNav';
import '../../../styles/predictions-page-styling/predictions.css';
import '../../../styles/predictions-page-styling/game-props.css';
import '../../../styles/stats-page-styling/scout-ai.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Attempt to repair a truncated JSON string by closing any open strings,
// arrays, and objects that were cut off mid-stream by the LLM.
function repairTruncatedJson(str) {
  const stack = [];
  let inStr = false;
  let esc = false;
  for (const ch of str) {
    if (esc)              { esc = false; continue; }
    if (ch === '\\' && inStr) { esc = true;  continue; }
    if (ch === '"')       { inStr = !inStr; continue; }
    if (inStr)            continue;
    if (ch === '{')       stack.push('}');
    else if (ch === '[')  stack.push(']');
    else if ((ch === '}' || ch === ']') && stack.length) stack.pop();
  }
  const suffix = (inStr ? '"' : '') + stack.reverse().join('');
  return JSON.parse(str + suffix);
}

function logoUrl(mlbId) {
  return `https://www.mlbstatic.com/team-logos/${mlbId}.svg`;
}

function getTeamMlbId(teamId) {
  return getTeamById(teamId)?.mlbId || null;
}

function fmtPitcher(name) {
  if (!name) return 'TBD';
  const parts = name.trim().split(/\s+/);
  return parts.length < 2 ? name : `${parts[0][0]}. ${parts.slice(1).join(' ')}`;
}

function fmtOdds(n) {
  if (n == null) return '—';
  return n > 0 ? `+${n}` : String(n);
}

function isFinal(game) {
  const s = (game.status || '').toLowerCase();
  return s === 'final' || s === 'game over' || s === 'completed';
}

function statusLabel(game) {
  if (isFinal(game)) return 'Final';
  const s = (game.status || '').toLowerCase();
  if (s.includes('progress') || s === 'live') return 'Live';
  return game.game_time_et || game.game_time || 'TBD';
}

// Parse a game time string ("8:05 PM ET" or "20:05") into minutes from midnight for sorting
function parseGameTimeMinutes(game) {
  const t = game.game_time_et || game.game_time || '';
  const ampm = t.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const mer = ampm[3].toUpperCase();
    if (mer === 'PM' && h !== 12) h += 12;
    if (mer === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  }
  const parts = t.split(':');
  if (parts.length >= 2) return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
  return 9999;
}

// ─── Prediction gate ──────────────────────────────────────────────────────────
// The server enforces the release gate now (a row simply isn't in the response
// until it's public, or — for admins only — arrives flagged `is_preview`), so
// "ready" just means a row with real data made it into `games`. See the games
// fetch effect below for how a gated game still gets an honest placeholder
// instead of vanishing.
function isReadyToShow(game, adminOverride = false) {
  return !!game.prediction && (adminOverride || !!game.scout_ai);
}

// ─── Mock prediction data (fallback) ─────────────────────────────────────────

function getMockPick(game) {
  const seed    = game.game_pk ?? game.id ?? 1;
  const pickAway = seed % 2 === 0;
  const conf     = 55 + (seed % 30);
  const awayFav  = pickAway;
  const awayML   = awayFav ? -(130 + (seed % 25)) : +(105 + (seed % 25));
  const homeML   = awayFav ? +(105 + (seed % 25)) : -(130 + (seed % 25));
  const books    = ['DraftKings', 'FanDuel', 'BetMGM', 'Caesars'];

  const mlPickBase  = awayFav ? awayML : homeML;
  const mlOppBase   = awayFav ? homeML : awayML;
  const rlPickBase  = awayFav ?  120 : -165;
  const f5PickBase  = awayFav ? -(118 + (seed % 18)) : +(100 + (seed % 18));
  const nrfiBase    = -(128 + (seed % 20));

  // Per-book offsets keep values realistic but distinct across books
  const offsets = [-3, 0, 4, -2];

  // Determine which side of the total is favored (higher avg odds = better for bettor)
  const overBase  = -110;
  const underBase = -110 + ((seed % 5) - 2); // varies -112 to -108 per game
  const totalSide = overBase >= underBase ? 'Over' : 'Under';
  const totalLine = 7.5 + (seed % 4);        // e.g. 7.5, 8.5, 9.5, 10.5
  const totalBase = totalSide === 'Over' ? overBase : underBase;

  // Model probability estimates per market (used for edge calculation)
  const modelProbs = {
    mlPick: conf,
    rlPick: Math.max(50, conf - 6 + (seed % 9)),
    total:  50 + (seed % 12),
    f5Pick: Math.max(50, conf - 4 + (seed % 7)),
    nrfi:   54 + (seed % 14),
  };

  // DFS platforms — show available Over/Under lines (null = market not offered)
  const dfsLineOffsets = [-0.5, 0, 0, 0.5]; // PrizePicks, Underdog, DK Pick6, Betr Picks

  return {
    pick: pickAway ? 'away' : 'home',
    confidence: conf,
    totalSide,
    totalLine,
    modelProbs,
    books: books.map((name, i) => ({
      name,
      mlPick:  mlPickBase  + offsets[i],
      mlOpp:   mlOppBase   - offsets[i],
      rlPick:  rlPickBase  + offsets[i],
      total:   totalBase   + offsets[i],
      f5Pick:  f5PickBase  + offsets[i],
      nrfi:    nrfiBase    + offsets[i],
    })),
    exchanges: [],
    dfs: ['PrizePicks', 'Underdog', 'DraftKings Pick6', 'Betr Picks'].map((name, i) => ({
      name,
      mlPick: null,
      rlPick: null,
      total:  totalLine + dfsLineOffsets[i],
      f5Pick: null,
      nrfi:   seed % 2 === i % 2 ? totalLine - 0.5 : null, // some platforms carry NRFI
    })),
    // Keep individual fields for the card summary display
    moneyline: { away: awayML, home: homeML },
  };
}

// ─── Real prediction → pick shape ─────────────────────────────────────────────
// Maps GamePredictionWithContextSchema fields onto the same pick shape used by
// all UI components. Falls back to getMockPick() for any field not yet on server.

function buildPick(row) {
  const pred       = row?.prediction;
  const odds       = row?.odds;
  const bookmakers = Array.isArray(row?.bookmakers) ? row.bookmakers : [];

  if (!pred && !odds && !bookmakers.length) return getMockPick(row);

  const mock  = getMockPick(row);
  const pHome = pred?.blended_p_home_win ?? pred?.p_home_win ?? 0.5;
  const pick  = pHome >= 0.5 ? 'home' : 'away';
  const conf  = Math.round(Math.max(pHome, 1 - pHome) * 100);

  const awayML = odds?.away_moneyline_game != null ? Math.round(odds.away_moneyline_game) : null;
  const homeML = odds?.home_moneyline_game != null ? Math.round(odds.home_moneyline_game) : null;

  const totalLine  = odds?.over_under_line_game ?? pred?.blended_predicted_total ?? pred?.predicted_total ?? mock.totalLine;
  // Lean toward the side the model predicts: if blended total > line → Over, else → Under
  const predTotal = pred?.blended_predicted_total ?? pred?.predicted_total ?? totalLine;
  const totalSide = predTotal >= totalLine ? 'Over' : 'Under';
  const totalProb = Math.min(70, 50 + Math.round(
    Math.abs(predTotal - totalLine) / (pred?.total_std_dev ?? 4) * 15
  ));

  // Helper to map a bookmaker/market entry to the shared odds shape
  const mapEntry = (b) => ({
    name:   b.bookmaker_title,
    mlPick: Math.round(pick === 'home' ? b.home_moneyline      : b.away_moneyline),
    mlOpp:  Math.round(pick === 'home' ? b.away_moneyline      : b.home_moneyline),
    rlPick: Math.round(pick === 'home' ? b.home_run_line_price : b.away_run_line_price),
    total:  Math.round(totalSide === 'Over' ? b.over_price     : b.under_price),
    f5Pick: b.f5_home_moneyline != null ? Math.round(pick === 'home' ? b.f5_home_moneyline : b.f5_away_moneyline) : null,
    nrfi:   b.nrfi_yes_price    != null ? Math.round(b.nrfi_yes_price) : null,
  });

  // Sportsbooks
  let books;
  if (bookmakers.length > 0) {
    books = bookmakers.map(mapEntry);
  } else {
    books = [];
  }

  // Prediction markets and DFS — real data only, empty array = hide tab
  const predMarkets = Array.isArray(row?.prediction_markets) ? row.prediction_markets : [];
  const exchanges   = predMarkets.map(mapEntry);
  const dfs         = Array.isArray(row?.dfs_props) ? row.dfs_props.map(mapEntry) : [];

  return {
    ...mock,
    pick, confidence: conf,
    totalSide, totalLine,
    modelProbs: {
      mlPick: conf,
      rlPick: Math.max(50, conf - 6),
      total:  totalProb,
      f5Pick: Math.max(50, conf - 4),
      nrfi:   mock.modelProbs.nrfi,
    },
    moneyline: { away: awayML, home: homeML },
    books,
    exchanges,
    dfs,
  };
}

// ─── Sportsbook config ────────────────────────────────────────────────────────

const BOOK_META = {
  DraftKings:     { abbr: 'DK',  color: '#62a800', bg: 'rgba(98,168,0,0.18)',    logo: 'https://logo.clearbit.com/draftkings.com' },
  FanDuel:        { abbr: 'FD',  color: '#1493ff', bg: 'rgba(20,147,255,0.18)',  logo: 'https://logo.clearbit.com/fanduel.com' },
  BetMGM:         { abbr: 'MGM', color: '#c8a84b', bg: 'rgba(200,168,75,0.18)',  logo: 'https://logo.clearbit.com/betmgm.com' },
  Caesars:        { abbr: 'CZR', color: '#0057b8', bg: 'rgba(0,87,184,0.18)',    logo: 'https://logo.clearbit.com/caesarssportsbook.com' },
  BetRivers:      { abbr: 'BR',  color: '#e63946', bg: 'rgba(230,57,70,0.18)',   logo: 'https://logo.clearbit.com/betrivers.com' },
  Fanatics:       { abbr: 'FAN', color: '#e84118', bg: 'rgba(232,65,24,0.18)',   logo: 'https://logo.clearbit.com/fanatics.com' },
  'Hard Rock Bet':{ abbr: 'HRB', color: '#ffd700', bg: 'rgba(255,215,0,0.18)',   logo: 'https://logo.clearbit.com/hardrock.com' },
  PointsBet:      { abbr: 'PB',  color: '#ff6b35', bg: 'rgba(255,107,53,0.18)',  logo: 'https://logo.clearbit.com/pointsbet.com' },
  BetUS:          { abbr: 'BUS', color: '#7c3aed', bg: 'rgba(124,58,237,0.18)',  logo: 'https://logo.clearbit.com/betus.com' },
};

function BookLogo({ name }) {
  const meta = BOOK_META[name] ?? { abbr: name.slice(0, 2).toUpperCase(), color: '#aaa', bg: 'rgba(170,170,170,0.15)', logo: null };
  const [err, setErr] = useState(false);
  if (!meta.logo || err) {
    return (
      <span className="gp-book-pill" style={{ background: meta.bg, color: meta.color }}>
        {meta.abbr}
      </span>
    );
  }
  return <img src={meta.logo} alt={name} className="gp-book-logo" onError={() => setErr(true)} />;
}

// Returns the row index with the best odds for the bettor in `colIndex`.
// Higher American odds value = better for the bettor in all cases.
function findBestBook(rows, colIndex) {
  if (colIndex == null || !rows.length) return -1;
  return rows.reduce((best, row, i, arr) =>
    row.values[colIndex] > arr[best].values[colIndex] ? i : best
  , 0);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ConfidenceBar({ pct }) {
  const color = pct >= 72 ? '#4ade80' : pct >= 62 ? '#facc15' : '#94a3b8';
  return (
    <div className="gp-conf-wrap">
      <div className="gp-conf-track">
        <div className="gp-conf-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="gp-conf-label" style={{ color }}>{pct}%</span>
    </div>
  );
}

// pickColIndex  — column index for the pick (null for totals with no direction)
// bestOverCol   — for Total: find best over (col 0) and best under (col 1) separately
// eslint-disable-next-line no-unused-vars
function OddsTable({ headers, rows, pickColIndex, isTotalTable = false }) {
  const bestPickRow  = findBestBook(rows, pickColIndex);
  const bestOverRow  = isTotalTable ? findBestBook(rows, 0) : -1;
  const bestUnderRow = isTotalTable ? findBestBook(rows, 1) : -1;

  return (
    <div className="gp-odds-table">
      {/* Header */}
      <div className="gp-odds-head">
        <span className="gp-odds-book-cell">Book</span>
        {headers.map((h, i) => (
          <span key={i} className={`gp-odds-cell ${pickColIndex === i ? 'pick-col' : ''}`}>{h}</span>
        ))}
        <span className="gp-odds-best-cell" />
      </div>

      {/* Rows */}
      {rows.map((row, ri) => {
        const isPickBest  = ri === bestPickRow && !isTotalTable;
        const isOverBest  = ri === bestOverRow  && isTotalTable;
        const isUnderBest = ri === bestUnderRow && isTotalTable;
        const isAnyBest   = isPickBest || isOverBest || isUnderBest;

        return (
          <div key={ri} className={`gp-odds-row ${isAnyBest ? 'best-row' : ''}`}>
            {/* Book logo + name */}
            <span className="gp-odds-book-cell">
              <BookLogo name={row.book} />
              <span className="gp-odds-book-name">{row.book}</span>
            </span>

            {/* Odds values */}
            {row.values.map((v, ci) => (
              <span key={ci} className={`gp-odds-cell ${pickColIndex === ci ? 'pick-col' : ''} ${v > 0 ? 'pos' : 'neg'}`}>
                {fmtOdds(v)}
              </span>
            ))}

            {/* Best badge */}
            <span className="gp-odds-best-cell">
              {isPickBest  && <span className="gp-best-badge">★ Best</span>}
              {isOverBest  && <span className="gp-best-badge over">★ Over</span>}
              {isUnderBest && ri !== bestOverRow && <span className="gp-best-badge under">★ Under</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Odds comparison matrix ───────────────────────────────────────────────────
// Rows = sportsbooks · Columns = all markets
// Best odds per column are highlighted · Book with most wins gets "Best Book" crown

const ALL_COLUMNS = [
  { key: 'mlPick',  label: 'ML',       accent: 'pick'  },
  { key: 'rlPick',  label: 'Run Line', accent: 'pick'  },
  { key: 'total',   label: 'Total',    accent: 'total' },
  { key: 'f5Pick',  label: 'F5 ML',    accent: 'pick'  },
  { key: 'nrfi',    label: 'NRFI',     accent: 'nrfi'  },
];

function OddsMatrix({ pick, pickName, oppName }) {
  // Only show columns where at least one book has real (non-null) data
  const columns = ALL_COLUMNS.filter(col =>
    pick.books.some(b => b[col.key] != null)
  );

  // For each column find the row index with the highest (best) value
  const bestRow = {};
  columns.forEach(col => {
    bestRow[col.key] = pick.books.reduce((best, book, i, arr) =>
      (book[col.key] ?? -Infinity) > (arr[best][col.key] ?? -Infinity) ? i : best
    , 0);
  });

  // Count how many "best" cells each book owns
  const winCount = pick.books.map((_, bi) =>
    Object.values(bestRow).filter(best => best === bi).length
  );
  const topBookIdx = winCount.indexOf(Math.max(...winCount));

  const gridStyle = { gridTemplateColumns: `180px repeat(${columns.length}, 1fr)` };

  return (
    <div className="gp-matrix-wrap">
      <div className="gp-matrix-outer">
        {/* Fixed header row */}
        <div className="gp-matrix-grid" style={gridStyle}>
          <div className="gp-matrix-corner">Sportsbook</div>
          {columns.map(col => (
            <div key={col.key} className={`gp-matrix-col-head accent-${col.accent}`}>
              <span className="gp-matrix-col-label">{col.label}</span>
              <span className="gp-matrix-col-sub">
                {col.key === 'total'   ? `${pick.totalSide} ${pick.totalLine}` :
                 col.accent === 'pick' ? pickName  :
                 col.accent === 'nrfi' ? '1st Inn' : ''}
              </span>
            </div>
          ))}
        </div>

        {/* Scrollable book rows */}
        <div className="gp-matrix-body">
          <div className="gp-matrix-grid" style={gridStyle}>
            {pick.books.map((book, bi) => (
              <>
                <div
                  key={`book-${bi}`}
                  className={`gp-matrix-book-cell ${bi === topBookIdx ? 'top-book' : ''}`}
                >
                  <BookLogo name={book.name} />
                  <span className="gp-matrix-book-name">{book.name}</span>
                  {bi === topBookIdx && (
                    <span className="gp-matrix-crown" title="Best overall book">👑</span>
                  )}
                </div>
                {columns.map(col => {
                  const val    = book[col.key];
                  const isBest = bestRow[col.key] === bi;
                  return (
                    <div
                      key={`${bi}-${col.key}`}
                      className={`gp-matrix-cell accent-${col.accent} ${isBest ? 'best' : ''} ${val > 0 ? 'pos' : 'neg'}`}
                    >
                      <span className="gp-matrix-odds">{fmtOdds(val)}</span>
                      {isBest && <span className="gp-matrix-best-dot" aria-label="Best odds" />}
                    </div>
                  );
                })}
              </>
            ))}
          </div>
        </div>
      </div>

      <div className="gp-matrix-legend">
        <span className="gp-legend-item pick">● Pick-side market</span>
        <span className="gp-legend-item total">● Total (favored side)</span>
        <span className="gp-legend-item best-dot">◆ Best odds</span>
        <span className="gp-legend-item crown">👑 Best overall book</span>
      </div>
    </div>
  );
}

// ─── Prediction exchange config ───────────────────────────────────────────────

const EXCHANGE_META = {
  BetOpenly: { abbr: 'BO',  color: '#e879f9', bg: 'rgba(232,121,249,0.15)', logo: 'https://logo.clearbit.com/betopenly.com' },
  Kalshi:    { abbr: 'KAL', color: '#00d4aa', bg: 'rgba(0,212,170,0.15)',   logo: 'https://logo.clearbit.com/kalshi.com' },
  Novig:     { abbr: 'NVG', color: '#fb923c', bg: 'rgba(251,146,60,0.15)',  logo: 'https://logo.clearbit.com/novig.com' },
  Polymarket:{ abbr: 'PM',  color: '#a78bfa', bg: 'rgba(167,139,250,0.15)', logo: 'https://logo.clearbit.com/polymarket.com' },
  ProphetX:  { abbr: 'PX',  color: '#38bdf8', bg: 'rgba(56,189,248,0.15)',  logo: 'https://logo.clearbit.com/prophetx.co' },
};

const DFS_META = {
  PrizePicks:         { abbr: 'PP',  color: '#818cf8', bg: 'rgba(129,140,248,0.15)', logo: 'https://logo.clearbit.com/prizepicks.com' },
  Underdog:           { abbr: 'UD',  color: '#f43f5e', bg: 'rgba(244,63,94,0.15)',   logo: 'https://logo.clearbit.com/underdogfantasy.com' },
  'DraftKings Pick6': { abbr: 'DK6', color: '#62a800', bg: 'rgba(98,168,0,0.15)',    logo: 'https://logo.clearbit.com/draftkings.com' },
  'Betr Picks':       { abbr: 'BTR', color: '#fbbf24', bg: 'rgba(251,191,36,0.15)',  logo: 'https://logo.clearbit.com/betr.com' },
};

function ExchangeLogo({ name }) {
  const meta = EXCHANGE_META[name] ?? DFS_META[name] ?? { abbr: name.slice(0, 2).toUpperCase(), color: '#aaa', bg: 'rgba(170,170,170,0.15)', logo: null };
  const [err, setErr] = useState(false);
  if (!meta.logo || err) {
    return (
      <span className="gp-book-pill" style={{ background: meta.bg, color: meta.color }}>
        {meta.abbr}
      </span>
    );
  }
  return <img src={meta.logo} alt={name} className="gp-book-logo" onError={() => setErr(true)} />;
}

// Best exchange = highest American odds value (same logic as sportsbooks)
function findBestExchange(exchanges, key) {
  return exchanges.reduce((best, ex, i, arr) =>
    (ex[key] ?? -Infinity) > (arr[best][key] ?? -Infinity) ? i : best
  , 0);
}

function ExchangeMatrix({ pick, pickName }) {
  const columns = ALL_COLUMNS.filter(col =>
    pick.exchanges.some(ex => ex[col.key] != null)
  );

  const bestRow = {};
  columns.forEach(col => {
    bestRow[col.key] = findBestExchange(pick.exchanges, col.key);
  });

  const winCount = pick.exchanges.map((_, ei) =>
    Object.values(bestRow).filter(best => best === ei).length
  );
  const topExIdx = winCount.indexOf(Math.max(...winCount));
  const gridStyle = { gridTemplateColumns: `180px repeat(${columns.length}, 1fr)` };

  return (
    <div className="gp-matrix-wrap">
      <div className="gp-exchange-note">
        Prediction market odds. Higher value = better price for bettors. Platforms may not offer all markets.
      </div>

      <div className="gp-matrix-outer">
        {/* Fixed header row */}
        <div className="gp-matrix-grid" style={gridStyle}>
          <div className="gp-matrix-corner">Platform</div>
          {columns.map(col => (
            <div key={col.key} className={`gp-matrix-col-head accent-${col.accent}`}>
              <span className="gp-matrix-col-label">{col.label}</span>
              <span className="gp-matrix-col-sub">
                {col.key === 'total'   ? `${pick.totalSide} ${pick.totalLine}` :
                 col.accent === 'pick' ? pickName  :
                 col.accent === 'nrfi' ? '1st Inn' : ''}
              </span>
            </div>
          ))}
        </div>

        {/* Scrollable exchange rows */}
        <div className="gp-matrix-body">
          <div className="gp-matrix-grid" style={gridStyle}>
            {pick.exchanges.map((ex, ei) => (
              <>
                <div
                  key={`ex-${ei}`}
                  className={`gp-matrix-book-cell ${ei === topExIdx ? 'top-book' : ''}`}
                >
                  <ExchangeLogo name={ex.name} />
                  <span className="gp-matrix-book-name">{ex.name}</span>
                  {ei === topExIdx && <span className="gp-matrix-crown" title="Best overall platform">👑</span>}
                </div>
                {columns.map(col => {
                  const val    = ex[col.key];
                  const isBest = bestRow[col.key] === ei;
                  return (
                    <div
                      key={`${ei}-${col.key}`}
                      className={`gp-matrix-cell accent-${col.accent} ${isBest ? 'best' : ''}`}
                    >
                      <span className="gp-matrix-odds">{fmtOdds(val)}</span>
                      {isBest && <span className="gp-matrix-best-dot" aria-label="Best price" />}
                    </div>
                  );
                })}
              </>
            ))}
          </div>
        </div>
      </div>

      <div className="gp-matrix-legend">
        <span className="gp-legend-item pick">● Pick-side market</span>
        <span className="gp-legend-item total">● Total (favored side)</span>
        <span className="gp-legend-item best-dot">◆ Best odds</span>
        <span className="gp-legend-item crown">👑 Best overall platform</span>
      </div>
    </div>
  );
}

// ─── DFS matrix ───────────────────────────────────────────────────────────────
// DFS platforms offer Over/Under lines (not odds). null = market not available.

// eslint-disable-next-line no-unused-vars
function DFSMatrix({ pick }) {
  const columns = ALL_COLUMNS.filter(col =>
    pick.dfs.some(p => p[col.key] != null)
  );
  const gridStyle = { gridTemplateColumns: `180px repeat(${columns.length}, 1fr)` };

  return (
    <div className="gp-matrix-wrap">
      <div className="gp-exchange-note">
        DFS platforms offer prop lines (More/Less). Markets not offered by a platform are shown as —.
      </div>

      <div className="gp-matrix-outer">
        {/* Fixed header row */}
        <div className="gp-matrix-grid" style={gridStyle}>
          <div className="gp-matrix-corner">Platform</div>
          {columns.map(col => (
            <div key={col.key} className={`gp-matrix-col-head accent-${col.accent}`}>
              <span className="gp-matrix-col-label">{col.label}</span>
              <span className="gp-matrix-col-sub">
                {col.key === 'total'   ? `${pick.totalSide} ${pick.totalLine}` :
                 col.accent === 'pick' ? 'Line' :
                 col.accent === 'nrfi' ? '1st Inn' : ''}
              </span>
            </div>
          ))}
        </div>

        {/* Scrollable DFS rows */}
        <div className="gp-matrix-body">
          <div className="gp-matrix-grid" style={gridStyle}>
            {pick.dfs.map((platform, pi) => (
              <>
                <div key={`dfs-${pi}`} className="gp-matrix-book-cell">
                  <ExchangeLogo name={platform.name} />
                  <span className="gp-matrix-book-name">{platform.name}</span>
                </div>
                {columns.map(col => {
                  const val = platform[col.key];
                  return (
                    <div
                      key={`${pi}-${col.key}`}
                      className={`gp-matrix-cell accent-${col.accent} ${val == null ? 'unavailable' : ''}`}
                    >
                      <span className="gp-matrix-odds">
                        {val == null ? '—' : col.key === 'total' || col.key === 'nrfi' ? val : '—'}
                      </span>
                    </div>
                  );
                })}
              </>
            ))}
          </div>
        </div>
      </div>

      <div className="gp-matrix-legend">
        <span className="gp-legend-item" style={{ color: 'rgba(255,255,255,0.35)' }}>— Market not offered by platform</span>
        <span className="gp-legend-item" style={{ color: 'rgba(255,255,255,0.35)' }}>Values shown are the prop line (More/Less)</span>
      </div>
    </div>
  );
}

// ─── Predictions panel (drawer default view) ──────────────────────────────────
// Every recommendation we have for this matchup in one place: the Scout AI
// scouting report's picks (ML / run line / total) plus any early-game props
// the day's board produced for this game.

// Scout odds arrive as strings ("-135"), board odds as numbers — normalize both
// and return null when absent so callers can skip the element entirely.
function fmtOddsLoose(v) {
  if (v == null || v === '') return null;
  const s = String(v);
  return s.startsWith('-') || s.startsWith('+') ? s : `+${s}`;
}

// Conviction arrives either as a 0–1 fraction or an already-1–5 rating.
function toFiveScale(v) {
  if (v == null) return 0;
  const n = Number(v);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(5, Math.round(n <= 1 ? n * 5 : n)));
}

function PredConfidence({ value }) {
  const n = toFiveScale(value);
  if (!n) return null;
  return (
    <span className="gp-pred-dots" aria-label={`Confidence ${n} of 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={`gp-pred-dot${i < n ? ' on' : ''}`} />
      ))}
    </span>
  );
}

// An empty picks[] on the early-game board is ambiguous on its own, so the
// board reports which kind of empty it is:
//
//   lockedAt null,  noEdgeReason null  → hasn't evaluated today's slate yet
//   lockedAt set,   noEdgeReason set   → evaluated, nothing cleared the bar
//   picks non-empty                    → normal
//
// The middle case is a real, informative result — "we looked, nothing was worth
// betting" — and is expected to be common on thin slates while these models are
// new and narrow-band. It must never render as an error or a loading state.
function earlyState({ picks, meta }) {
  if (Array.isArray(picks) && picks.length > 0) return 'picks';
  if (meta?.noEdgeReason) return 'no-edge';
  return 'pending';
}

// Shown in place of the pick list on a no-edge day. `reason` is a complete
// sentence from the server and varies by day — render it verbatim.
function NoEdgeCard({ reason }) {
  return (
    <div className="gp-no-edge">
      <span className="gp-no-edge-title">No Edge Today</span>
      <p className="gp-no-edge-reason">{reason}</p>
    </div>
  );
}

// Starting pitchers off an early-game pick. NRFI/YRFI picks carry the full
// `nrfiProfile`; F5 picks carry a lighter `f5Profile` with names only. Both
// are optional — the feed predates them, and a pick may arrive without either.
function startersOf(pick) {
  const ctx = pick?.reasoning?.context;
  const prof = ctx?.nrfiProfile || ctx?.f5Profile;
  if (!prof) return null;
  const away = prof.awaySp?.name;
  const home = prof.homeSp?.name;
  if (!away && !home) return null;
  return `${away || 'TBD'} vs ${home || 'TBD'}`;
}

function PredCard({ type, pick, confidence, odds, meta, badge, badgeLevel, sub }) {
  const o = fmtOddsLoose(odds);
  return (
    <div className="gp-pred-card">
      <div className="gp-pred-card-head">
        <span className="gp-pred-card-type">{type}</span>
        {badge && (
          <span className={`gp-pred-card-badge${badgeLevel ? ` ${badgeLevel}` : ''}`}>
            {badge}
          </span>
        )}
      </div>
      <span className="gp-pred-card-pick">{pick}</span>
      {sub && <span className="gp-pred-card-sub">{sub}</span>}
      <div className="gp-pred-card-foot">
        <PredConfidence value={confidence} />
        {o && (
          <span className="gp-pred-card-odds">
            <span className={o.startsWith('-') ? 'neg' : 'pos'}>{o}</span>
            {meta && <span className="gp-pred-card-meta">{meta}</span>}
          </span>
        )}
        {!o && meta && <span className="gp-pred-card-meta">{meta}</span>}
      </div>
    </div>
  );
}

function PredictionsPanel({ game, earlyPicks, earlyMeta, ready }) {
  const scoutPicks = ready ? (game.scout_ai?.picks ?? []) : [];
  const hasScout = scoutPicks.length > 0;
  // A day-level no-edge verdict applies to every matchup, so it shows here even
  // though earlyPicks is scoped to this game.
  const earlySt   = earlyState({ picks: earlyPicks, meta: earlyMeta });
  const hasEarly  = earlySt === 'picks';
  const noEdge    = earlySt === 'no-edge';

  if (!hasScout && !hasEarly && !noEdge) {
    return (
      <div className="gp-odds-unavailable">
        <span className="gp-odds-unavailable-icon">📋</span>
        <p>No recommendations yet for this matchup</p>
        <p className="gp-odds-unavailable-sub">
          Scout AI picks and early-game props post closer to first pitch ({statusLabel(game)})
        </p>
      </div>
    );
  }

  return (
    <div className="gp-pred-panel">
      {hasScout && (
        <section className="gp-pred-section">
          <h4 className="gp-pred-section-title">Scouting Report Picks</h4>
          <div className="gp-pred-cards">
            {scoutPicks.map((p, i) => (
              <PredCard
                key={`scout-${i}`}
                type={p.type ?? '—'}
                pick={p.pick ?? '—'}
                confidence={p.confidence}
                odds={p.best_odds}
                meta={p.best_odds_book}
              />
            ))}
          </div>
        </section>
      )}

      {noEdge && (
        <section className="gp-pred-section">
          <h4 className="gp-pred-section-title">Early Game Props</h4>
          <NoEdgeCard reason={earlyMeta.noEdgeReason} />
        </section>
      )}

      {hasEarly && (
        <section className="gp-pred-section">
          <h4 className="gp-pred-section-title">Early Game Props</h4>
          <div className="gp-pred-cards">
            {earlyPicks.map((p, i) => (
              <PredCard
                key={p.id ?? `early-${i}`}
                // Label off the pick's own market/statType — F5 moneyline, run
                // line and total land on this same feed, so nothing here may
                // assume NRFI is the only market.
                type={p.market || p.statType || 'Early game'}
                pick={p.selection ?? p.side ?? '—'}
                confidence={p.conviction}
                odds={p.odds}
                meta={p.ev != null ? `${p.ev > 0 ? '+' : ''}${p.ev}% EV` : null}
                // Unlike the other boards, picks[] here is NOT pre-filtered to
                // "strong" — colour each badge by its own level so a split pick
                // never reads like a clean edge.
                badge={p.consensus?.label ?? null}
                badgeLevel={p.consensus?.level ?? null}
                sub={startersOf(p)}
              />
            ))}
          </div>
          {/* This feed carries no deep_links yet — odds stay plain, no betslip. */}
        </section>
      )}
    </div>
  );
}

// ─── Right-side drawer modal ──────────────────────────────────────────────────

function OddsDrawer({ game, onClose, adminOverride = false, earlyProps = [], earlyMeta = null }) {
  const navigate   = useNavigate();
  const pick       = buildPick(game);
  const ready      = isReadyToShow(game, adminOverride);
  const awayAbbr   = getTeamById(game.away_team_id)?.id || game.away_team_name;
  const homeAbbr   = getTeamById(game.home_team_id)?.id || game.home_team_name;
  const awayMlbId  = getTeamMlbId(game.away_team_id);
  const homeMlbId  = getTeamMlbId(game.home_team_id);
  const awayName   = getTeamById(game.away_team_id)?.name || game.away_team_name;
  const homeName   = getTeamById(game.home_team_id)?.name || game.home_team_name;

  // Use Scout AI moneyline pick; fall back to model pick
  const drawerScoutMlPick  = (game.scout_ai?.picks ?? []).find(p => p.type === 'Moneyline');
  const drawerScoutPickSide = drawerScoutMlPick?.pick
    ? (drawerScoutMlPick.pick.toLowerCase().includes((game.away_team_name ?? '').toLowerCase()) ||
       (game.away_team_name ?? '').toLowerCase().includes(drawerScoutMlPick.pick.toLowerCase())
        ? 'away' : 'home')
    : null;
  const drawerPickSide = drawerScoutPickSide ?? pick.pick;
  const pickedName = drawerPickSide === 'away' ? awayName : homeName;
  const pickAbbr   = drawerPickSide === 'away' ? awayAbbr : homeAbbr;
  const oppAbbr    = drawerPickSide === 'away' ? homeAbbr : awayAbbr;

  // Everything we recommend for this matchup leads; the odds matrices sit
  // behind it. 'predictions' | 'books' | 'exchange'
  const [view, setView] = useState('predictions');

  // The early-game feed is a day-level board — scope it to this matchup.
  // Picks on these boards key the game as `gamePk` (camelCase) while game rows
  // from /predictions/today use `game_pk`; accept either spelling so a backend
  // that sends the snake_case variant degrades to "no picks on the page board"
  // rather than silently emptying this panel only.
  const earlyPicks = (Array.isArray(earlyProps) ? earlyProps : [])
    .filter(p => {
      const pk = p?.gamePk ?? p?.game_pk;
      return pk != null && String(pk) === String(game.game_pk);
    });

  const [scoutAnalysis,   setScoutAnalysis]   = useState(null);
  const [scoutLoading]                         = useState(false);
  const [scoutError,      setScoutError]       = useState(null);
  const [showScoutModal,  setShowScoutModal]   = useState(false);

  const handleScoutClick = useCallback(() => {
    setShowScoutModal(true);
    if (scoutAnalysis) return;
    // Use scout_ai already embedded on the game object from predictions/today —
    // avoids the localStorage cache which can serve stale pick directions.
    const ai = game.scout_ai ?? null;
    if (!ai) {
      setScoutError('Scout AI analysis is not yet available for this game');
      return;
    }
    setScoutAnalysis(ai);
  }, [game.scout_ai, scoutAnalysis]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return createPortal(
    <div className="gp-drawer-overlay" onClick={onClose} aria-modal="true" role="dialog">
      <div className="gp-drawer" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="gp-drawer-header">
          <div className="gp-drawer-matchup">
            {awayMlbId && <img src={logoUrl(awayMlbId)} alt={game.away_team_name} className="gp-drawer-logo" />}
            <div className="gp-drawer-teams">
              <span className="gp-drawer-team-name">{awayName}</span>
              <span className="gp-drawer-vs">@ {homeName}</span>
            </div>
            {homeMlbId && <img src={logoUrl(homeMlbId)} alt={game.home_team_name} className="gp-drawer-logo" />}
          </div>
          <div className="gp-drawer-subrow">
            <span className="gp-drawer-time">{statusLabel(game)}</span>
            {ready && (
              <div className="gp-drawer-pick-chip">
                <span className="gp-pick-badge">LEAN</span>
                <span className="gp-drawer-pick-name">{pickedName}</span>
                <span className="gp-drawer-conf">{pick.confidence}%</span>
              </div>
            )}
          </div>
          {game.is_preview && (
            <div className="pred-preview-badge-row">
              <span className="pred-preview-badge">{game.preview_badge || DEFAULT_PREVIEW_BADGE}</span>
            </div>
          )}
          <button className="gp-drawer-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* View toggle */}
        <div className="gp-view-toggle">
          {ready && (
            <ScoutAiButton
              isToday={true}
              hasAnalysis={!!game.scout_ai}
              loading={false}
              onClick={handleScoutClick}
            />
          )}
          <div className="gp-view-toggle-right">
            <button
              className="gp-view-toggle-btn"
              onClick={() => { onClose(); navigate(`/mlb-schedule/${game.game_pk}`); }}
            >
              Matchup Detail
            </button>
            <button
              className={`gp-view-toggle-btn ${view === 'predictions' ? 'active' : ''}`}
              onClick={() => setView('predictions')}
            >
              Predictions
            </button>
            <button
              className={`gp-view-toggle-btn ${view === 'books' ? 'active' : ''}`}
              onClick={() => setView('books')}
            >
              Sportsbooks
            </button>
            {pick.exchanges.length > 0 && (
              <button
                className={`gp-view-toggle-btn ${view === 'exchange' ? 'active' : ''}`}
                onClick={() => setView('exchange')}
              >
                Pred. Exchange
              </button>
            )}
          </div>
        </div>

        {/* Scout AI Modal */}
        <ScoutAiModal
          isOpen={showScoutModal}
          onClose={() => setShowScoutModal(false)}
          analysis={scoutAnalysis}
          error={scoutError}
          loading={scoutLoading}
          onRetry={handleScoutClick}
          generatedAt={game.prediction?.created_at}
        />

        {/* Matrix body */}
        <div className="gp-drawer-body">
          {view === 'predictions' && (
            <PredictionsPanel game={game} earlyPicks={earlyPicks} earlyMeta={earlyMeta} ready={ready} />
          )}
          {view === 'books' && (
            pick.books.length > 0
              ? <OddsMatrix pick={pick} pickName={pickAbbr} oppName={oppAbbr} />
              : (
                <div className="gp-odds-unavailable">
                  <span className="gp-odds-unavailable-icon">⏳</span>
                  <p>Odds not yet available</p>
                  <p className="gp-odds-unavailable-sub">Check back closer to game time ({statusLabel(game)})</p>
                </div>
              )
          )}
          {view === 'exchange' && <ExchangeMatrix pick={pick} pickName={pickAbbr} />}
        </div>

      </div>
    </div>,
    document.body
  );
}

// ─── Not-public-yet card ───────────────────────────────────────────────────────
// For a non-admin caller, a gated game's row is simply missing from
// /predictions/today — so this card is built from whatever still names the
// matchup pre-release (the pitchers/today feed never omits the game row; see
// buildGames()). No prediction data exists to show, and none is faked.
function NotPublicYetCard({ game }) {
  const away = game.away_team_name || 'Away';
  const home = game.home_team_name || 'Home';
  const time = game.game_time_et || game.game_time || 'TBD';

  return (
    <div className="gp-card gp-card--muted gp-card--not-public" aria-disabled="true">
      <div className="gp-card-topbar">
        <span className="gp-card-time">{time}</span>
        <span className="gp-card-status-dot" />
      </div>
      <div className="gp-card-not-public-matchup">{away} @ {home}</div>
      <div className="gp-card-footer gp-card-not-public-footer">
        <img src={loadingPredictionsIcon} alt="" className="gp-card-not-public-icon" aria-hidden="true" />
        <span className="pred-gated-note">Not public yet — check back closer to first pitch ({time})</span>
      </div>
    </div>
  );
}

// ─── Game card ────────────────────────────────────────────────────────────────

function GamePickCard({ game, isSelected, onSelect, adminOverride = false }) {
  const pick       = buildPick(game);
  const ready      = isReadyToShow(game, adminOverride);
  const awayMlbId  = getTeamMlbId(game.away_team_id);
  const homeMlbId  = getTeamMlbId(game.home_team_id);
  const awayAbbr   = getTeamById(game.away_team_id)?.id || game.away_team_name;
  const homeAbbr   = getTeamById(game.home_team_id)?.id || game.home_team_name;
  // Drive is-pick from Scout AI moneyline recommendation; fall back to model pick
  const scoutMlPick  = (game.scout_ai?.picks ?? []).find(p => p.type === 'Moneyline');
  const scoutPickSide = scoutMlPick?.pick
    ? (scoutMlPick.pick.toLowerCase().includes((game.away_team_name ?? '').toLowerCase()) ||
       (game.away_team_name ?? '').toLowerCase().includes(scoutMlPick.pick.toLowerCase())
        ? 'away' : 'home')
    : null;
  const pickSide   = scoutPickSide ?? pick.pick;
  const awayIsPick = ready && pickSide === 'away';
  const homeIsPick = ready && pickSide === 'home';

  return (
    <button
      className={`gp-card ${isSelected ? 'selected' : ''} ${!ready ? 'gp-card--muted' : ''}`}
      data-pick={ready ? pickSide : null}
      onClick={onSelect}
      aria-pressed={isSelected}
    >
      {/* Top bar: time + status */}
      <div className="gp-card-topbar">
        <span className="gp-card-time">{statusLabel(game)}</span>
        <span className="gp-card-status-dot" />
      </div>

      {/* Team logos */}
      <div className="gp-card-teams">
        <div className={`gp-card-side ${awayIsPick ? 'is-pick' : ''}`}>
          {awayMlbId && <img src={logoUrl(awayMlbId)} alt={game.away_team_name} className="gp-card-logo" />}
          <span className="gp-card-abbr">{awayAbbr}</span>
          {pick.moneyline.away != null && (
            <span className={`gp-card-odds ${pick.moneyline.away > 0 ? 'pos' : 'neg'}`}>
              {fmtOdds(pick.moneyline.away)}
            </span>
          )}
          {awayIsPick && <span className="gp-pick-badge">LEAN</span>}
        </div>

        <div className="gp-card-vs">
          <span>@</span>
          <span className="gp-card-pitcher-vs">{fmtPitcher(game.away_sp_name)} vs {fmtPitcher(game.home_sp_name)}</span>
        </div>

        <div className={`gp-card-side right ${homeIsPick ? 'is-pick' : ''}`}>
          {homeIsPick && <span className="gp-pick-badge">LEAN</span>}
          {pick.moneyline.home != null && (
            <span className={`gp-card-odds ${pick.moneyline.home > 0 ? 'pos' : 'neg'}`}>
              {fmtOdds(pick.moneyline.home)}
            </span>
          )}
          <span className="gp-card-abbr">{homeAbbr}</span>
          {homeMlbId && <img src={logoUrl(homeMlbId)} alt={game.home_team_name} className="gp-card-logo" />}
        </div>
      </div>

      {/* Confidence bar or pending notice */}
      <div className="gp-card-footer">
        {ready ? (
          <ConfidenceBar pct={pick.confidence} />
        ) : adminOverride && pick.confidence ? (
          <ConfidenceBar pct={pick.confidence} />
        ) : !adminOverride ? (
          <span className="gp-card-pred-loading">⏳ Predictions coming soon</span>
        ) : null}
      </div>
      {game.is_preview && (
        <div className="pred-preview-badge-row">
          <span className="pred-preview-badge">{game.preview_badge || DEFAULT_PREVIEW_BADGE}</span>
        </div>
      )}
    </button>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="gp-card gp-card-skeleton" aria-hidden="true">
      <div className="gp-card-topbar">
        <div className="sk sk-text narrow" />
      </div>
      <div className="gp-card-teams">
        <div className="gp-card-side">
          <div className="sk sk-logo" />
          <div className="sk sk-text narrow" />
        </div>
        <div className="gp-card-vs"><span>@</span></div>
        <div className="gp-card-side right">
          <div className="sk sk-text narrow" />
          <div className="sk sk-logo" />
        </div>
      </div>
      <div className="gp-card-footer">
        <div className="sk sk-text wide" style={{ width: '100%' }} />
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function GameProps() {
  const { isAdmin, profileLoading } = useAuth();
  const [games, setGames]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [bestProps, setBestProps]   = useState([]);   // server-frozen top 3 game props
  const [earlyProps, setEarlyProps] = useState([]);   // early-game props (NRFI/YRFI, F5 …)
  // `lockedAt` + `noEdgeReason` disambiguate an empty picks[]: see earlyState().
  const [earlyMeta, setEarlyMeta]   = useState({ lockedAt: null, noEdgeReason: null });
  const [openPick, setOpenPick]     = useState(null);  // War Room drawer

  useEffect(() => {
    // Best Game Props board — additive sibling to /predictions/today. Empty until
    // the day's board generates; hide the panel on empty/error (never block).
    predictionsService.getBestGameProps()
      .then(res => setBestProps(Array.isArray(res?.picks) ? res.picks : []))
      .catch(() => setBestProps([]));

    // Early-game props (markets resolving before the full game — NRFI/YRFI now,
    // F5 markets soon). Brand-new model with a narrow qualifying band, so empty
    // is the common case and a normal state, not an error.
    predictionsService.getBestEarlyGameProps()
      .then(res => {
        setEarlyProps(Array.isArray(res?.picks) ? res.picks : []);
        setEarlyMeta({ lockedAt: res?.lockedAt ?? null, noEdgeReason: res?.noEdgeReason ?? null });
      })
      .catch(() => {
        setEarlyProps([]);
        // A failed request is NOT a no-edge day — leave the reason null so the
        // panel hides rather than claiming the desk evaluated the slate.
        setEarlyMeta({ lockedAt: null, noEdgeReason: null });
      });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    // /predictions/today omits a game's row entirely until it clears the
    // release gate (for a non-admin caller) — so it's empty by design during
    // the pre-release window, not an error. /predictions/pitchers/today never
    // omits the game row (only nulls the pitcher slots), so it's the one feed
    // that always knows which games exist today — used here only to fill the
    // gap with an honest placeholder, never to fabricate prediction data.
    Promise.all([
      predictionsService.getToday(),
      predictionsService.getPitchersToday().catch(() => []),
    ])
      .then(([rows, pitcherRows]) => {
        const filtered = Array.isArray(rows)
          ? rows.filter(r => r.season_type !== 'spring' && r.season_type !== 'S' && !isFinal(r))
          : [];
        // Deduplicate true duplicate entries (same game rescheduled).
        // Doubleheaders share team IDs but have distinct game_pks or game_num — preserve both.
        // Composite key: game_pk + game_num (if present) distinguishes DH games while
        // collapsing true dupes (same game_pk, same game_num, different stale API entry).
        const seen = new Map();
        for (const r of filtered) {
          const gameNum = r.game_num ?? r.game_number ?? '';
          const key = `${r.game_pk ?? r.id}_${gameNum}`;
          const existing = seen.get(key);
          // Prefer the entry with a prediction attached; otherwise keep the latest time
          if (!existing || (!existing.prediction && r.prediction)) {
            seen.set(key, r);
          }
        }
        // Normalize scout_ai: new LLM wraps the analysis as { raw: "...JSON string..." }
        // Parse raw into the object so all downstream code works unchanged.
        // If the LLM output was truncated, attempt to repair before giving up.
        const normalize = (r) => {
          if (r.scout_ai?.raw && typeof r.scout_ai.raw === 'string') {
            try {
              return { ...r, scout_ai: JSON.parse(r.scout_ai.raw) };
            } catch {
              try {
                return { ...r, scout_ai: repairTruncatedJson(r.scout_ai.raw) };
              } catch {
                return { ...r, scout_ai: r.scout_ai.raw };
              }
            }
          }
          return r;
        };
        const today = [...seen.values()].map(normalize);
        const knownPks = new Set(today.map(r => r.game_pk ?? r.id));

        const placeholders = (Array.isArray(pitcherRows) ? pitcherRows : [])
          .filter(p => p.game_pk != null && !knownPks.has(p.game_pk))
          .map(p => ({
            id: p.game_pk, game_pk: p.game_pk, date: p.date,
            game_time_et: p.game_time_et, status: 'Scheduled',
            away_team_name: p.away_team_name, home_team_name: p.home_team_name,
            __notPublic: true,
          }));

        const merged = [...today, ...placeholders]
          .sort((a, b) => parseGameTimeMinutes(a) - parseGameTimeMinutes(b));
        setGames(merged);
      })
      .catch(() => setGames([]))
      .finally(() => setLoading(false));
  }, []);

  const handleCardClick = useCallback((gameId) => {
    setSelectedId(prev => prev === gameId ? null : gameId);
  }, []);

  const handleClose = useCallback(() => setSelectedId(null), []);

  const selectedGame     = games.find(g => (g.id ?? g.game_pk) === selectedId) ?? null;

  const todayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  return (
    <div className="predictions-page">
      {/* Header */}
      <div className="predictions-header">
        <div className="predictions-header-inner">
          <h1>Game Props</h1>
          <PredictionsNav />
        </div>
      </div>

      {/* Content */}
      <div className="predictions-content">
        <div className="gp-date-label">{todayLabel}</div>

        <BestPropsBoard title="Best Game Props" subtitle="Scout AI · frozen for today" picks={bestProps} onOpen={setOpenPick} />
        {/* Early-game props. Three outcomes: real picks, an explicit no-edge
            verdict for the day, or not-yet-evaluated (render nothing). */}
        {earlyState({ picks: earlyProps, meta: earlyMeta }) === 'no-edge' ? (
          <div className="sd-bp">
            <div className="sd-bp-head">
              <span className="sd-bp-dot" />
              Best Early Game Props
              <span className="sd-bp-sub">Resolves before the full game</span>
            </div>
            <NoEdgeCard reason={earlyMeta.noEdgeReason} />
          </div>
        ) : (
          <BestPropsBoard title="Best Early Game Props" subtitle="Resolves before the full game" picks={earlyProps} onOpen={setOpenPick} />
        )}

        {/* Card grid */}
        {loading || profileLoading ? (
          <>
            <div className="gp-loading-banner">
              <span className="gp-loading-spinner" />
              <span>Predictions loading</span>
            </div>
            <div className="gp-grid">
              {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          </>
        ) : games.length === 0 ? (
          <div className="gp-empty">
            <span className="gp-empty-icon">⚾</span>
            <p>No games scheduled for today.</p>
          </div>
        ) : (
          <>
            <div className="pp-divider">
              <span className="pp-divider-line" />
              <span className="pp-divider-label">
                {games.length} game{games.length !== 1 ? 's' : ''} · Select a card to see full odds
              </span>
              <span className="pp-divider-line" />
            </div>
            <div className="gp-grid">
              {games.map(game => {
                const gid = game.id ?? game.game_pk;
                if (game.__notPublic) {
                  return <NotPublicYetCard key={gid} game={game} />;
                }
                return (
                  <GamePickCard
                    key={gid}
                    game={game}
                    isSelected={selectedId === gid}
                    onSelect={() => handleCardClick(gid)}
                    adminOverride={isAdmin}
                  />
                );
              })}
            </div>

            <p className="gp-disclaimer">
              * Prediction data is model-generated and for informational purposes only.
              Odds sourced from major US sportsbooks. Please gamble responsibly.
            </p>
          </>
        )}
      </div>

      {/* Right-side drawer modal */}
      {selectedGame && (
        <OddsDrawer
          game={selectedGame}
          onClose={handleClose}
          adminOverride={isAdmin}
          earlyProps={earlyProps}
          earlyMeta={earlyMeta}
        />
      )}

      {/* Shared Scout Desk War Room — opened from a Best Game Props card */}
      {openPick && <WarRoom play={openPick} onClose={() => setOpenPick(null)} />}
    </div>
  );
}
